import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import { useParams } from "react-router-dom";
import {
  clearAdminTonneDefektItem,
  getAdminTonneDefektItemById,
  updateStatusById,
} from "../../store/slices/tonneDefect";
import { setDataLoading } from "../../store/slices/admin";
import { format, getWeek } from "date-fns";
import { de } from "date-fns/locale";
import {
  AdminContentWrapper,
  AdminDetailInfoItem,
  AdminDetailPageHeader,
  AdminDetailsPageItemName,
  Button,
  Loader,
  History,
  CheckboxV2,
  CustomInputV2,
  TextAreaV2,
  // CustomDatePicker,
} from "../../components";
import transformHexToRgba from "../../util/transformHexToRgba";
import appConfig from "../../util/appConfig";
import DatePicker, { registerLocale } from "react-datepicker";
import style from "./style.module.scss";
import { statuses } from "../AdminTonneDefekt";

registerLocale("de", de);

const AdminTonneDefektById = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams();

  const { isAdminLoaderActive } = useAppSelector((state) => state.admin);
  const { isTonneDefektDataLoading, tonneDefektDataItem } = useAppSelector(
    (state) => state.tonneDefekt.admin,
  );

  const [selectedCheckbox, setSelectedCheckbox] = useState<
    undefined | "nextWeek" | "calenderWeek" | "toenisvorst" | "stornieren"
  >(undefined);
  const [calendarWeek, setCalendarWeek] = useState(0);
  const [toenisvorstWeekDay, setToenisvorstWeekDay] = useState("Montag");
  const [toenisvorstWeekDate, setToenisvorstWeekDate] = useState<Date | null>(
    null,
  );
  const [text, setText] = useState("");

  const generateCalenderWeek = (
    selectedCheckbox:
      | undefined
      | "nextWeek"
      | "calenderWeek"
      | "toenisvorst"
      | "stornieren",
  ) => {
    switch (selectedCheckbox) {
      case "nextWeek": {
        return (getWeek(new Date()) + 1).toString();
      }
      case "calenderWeek": {
        return calendarWeek.toString();
      }
      case "toenisvorst": {
        if (toenisvorstWeekDate) {
          return getWeek(new Date(toenisvorstWeekDate)).toString();
        }
        return undefined;
      }
      default: {
        return undefined;
      }
    }
  };
  const generateMessage = (
    selectedCheckbox:
      | undefined
      | "nextWeek"
      | "calenderWeek"
      | "toenisvorst"
      | "stornieren",
  ) => {
    switch (selectedCheckbox) {
      case "nextWeek": {
        return `Der Auftrag wird nächste Woche durchgeführt (in Kalenderwoche ${getWeek(new Date()) + 1}).`;
      }
      case "calenderWeek": {
        return `Der Auftrag wird voraussichtlich in Kalenderwoche ${calendarWeek} durchgeführt.`;
      }
      case "toenisvorst": {
        if (toenisvorstWeekDate) {
          return `Der Auftrag wird voraussichtlich am ${toenisvorstWeekDay} ${format(new Date(toenisvorstWeekDate), "dd.MM.yyyy")} durchgeführt.`;
        }
        return undefined;
      }
      case "stornieren": {
        return text;
      }
      default: {
        return undefined;
      }
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getAdminTonneDefektItemById(id));
    }
    return () => {
      dispatch(clearAdminTonneDefektItem());
      dispatch(setDataLoading(false));
    };
  }, []);

  const items = [
    {
      title: "",
      items: [
        {
          rowTitle: "Status",
          rowInfo: statuses[tonneDefektDataItem?.status!],
        },
        {
          rowTitle: "Datum (Meldung)",
          rowInfo: tonneDefektDataItem
            ? format(
                new Date(tonneDefektDataItem?.createdAt),
                "dd.MM.yyyy, HH:mm",
                {
                  locale: de,
                },
              )
            : "",
        },
        {
          rowTitle: "KW",
          rowInfo: tonneDefektDataItem?.calenderWeek ?? "-",
        },
        {
          rowTitle: "Nachricht",
          rowInfo: tonneDefektDataItem?.adminMessage ?? "-",
          isLast: true,
        },
      ],
    },
    {
      title: "Kontaktdaten",
      items: [
        {
          rowTitle: "Name",
          rowInfo: `${tonneDefektDataItem?.userName} ${tonneDefektDataItem?.userSurname}`,
        },
        {
          rowTitle: "Straße",
          rowInfo: `${tonneDefektDataItem?.street}
                      ${tonneDefektDataItem?.homeNumber}
                      ${
                        tonneDefektDataItem?.hausNrZusatz
                          ? `${tonneDefektDataItem?.hausNrZusatz}`
                          : ""
                      }`,
        },
        {
          rowTitle: "Ort",
          rowInfo: `${tonneDefektDataItem?.place}`,
        },
        {
          rowTitle: "Telefonnummer",
          rowInfo: `${
            tonneDefektDataItem?.phoneNumber
              ? "+" + tonneDefektDataItem?.phoneNumber
              : "-"
          }`,
        },
        {
          rowTitle: "E-Mail",
          rowInfo: `${tonneDefektDataItem?.email}`,
        },
        {
          rowTitle: "Kassenzeichen",
          rowInfo: `${tonneDefektDataItem?.kassenzeichen ?? ""}`,
          isLast: true,
        },
      ],
    },
    {
      title: "Tonneninformation",
      items: [
        {
          rowTitle: "Objekt ID",
          rowInfo: `${tonneDefektDataItem?.objectId ?? ""}`,
        },
        {
          rowTitle: "Nachricht",
          rowInfo: `${tonneDefektDataItem?.message ?? "-"}`,
        },
        ...(["viersen"].includes(appConfig.logo)
          ? [
              {
                rowTitle: "Aufkleber",
                rowInfo: tonneDefektDataItem?.tonnenNummer
                  ? tonneDefektDataItem?.tonnenNummer
                  : "-",
              },
            ]
          : []),
        ...(["mags"].includes(appConfig.logo)
          ? [
              {
                rowTitle: "Behälternummer",
                rowInfo: tonneDefektDataItem?.inventarnr ?? "",
              },
            ]
          : []),
        {
          rowTitle: "Tonnentyp",
          rowInfo: `${tonneDefektDataItem?.tonnenType}`,
        },
        {
          rowTitle: "Tonnenvolumen",
          rowInfo: `${tonneDefektDataItem?.tonnenVolume}L`,
        },
        {
          rowTitle: "Grund",
          rowInfo: `${tonneDefektDataItem?.tonnenDefectReason}`,
        },
        {
          rowTitle: "Interne Behälternummer",
          rowInfo: `${tonneDefektDataItem?.behid ?? "-"}`,
        },
        {
          rowTitle: "KAV-Nummer",
          rowInfo: `${tonneDefektDataItem?.kavnr ?? "-"}`,
        },
      ],
    },
  ];

  const checkboxesToMap = [
    {
      onChange: () => {
        // setIsWeekSelected(true);
        setSelectedCheckbox("nextWeek");
      },
      text: "Der Auftrag wird nächste Woche durchgeführt.",
      value: selectedCheckbox === "nextWeek",
      withTextInput: false,
    },
    {
      onChange: () => {
        // setIsWeekSelected(false);
        setSelectedCheckbox("calenderWeek");
      },
      text: `Der Auftrag wird voraussichtlich in Kalenderwoche ${calendarWeek} durchgeführt.`,
      value: selectedCheckbox === "calenderWeek",
      withTextInput: true,
    },
    ...(["viersen"].includes(appConfig.logo)
      ? [
          {
            onChange: () => {
              // setIsWeekSelected(false);
              setSelectedCheckbox("toenisvorst");
            },
            text: "Tönisvorst",
            value: selectedCheckbox === "toenisvorst",
            withCalendarInput: true,
          },
        ]
      : []),
    {
      onChange: () => {
        // setIsWeekSelected(false);
        setSelectedCheckbox("stornieren");
      },
      text: "Stornieren",
      value: selectedCheckbox === "stornieren",
      withTextAreaInput: true,
    },
  ];

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      <AdminDetailPageHeader isBtnAvailable={false} />
      <AdminContentWrapper>
        <AdminDetailsPageItemName text="Detailinformationen" />
        {isTonneDefektDataLoading ? null : !tonneDefektDataItem ? null : (
          <div>
            {items.map((el, idx) => {
              return (
                <AdminDetailInfoItem
                  items={el.items}
                  title={el.title}
                  key={idx}
                />
              );
            })}
            {tonneDefektDataItem?.history?.length ? (
              <History history={tonneDefektDataItem?.history} />
            ) : null}
            <div
              style={{
                display:
                  tonneDefektDataItem?.status === "accepted" ? "none" : "flex",
                flexDirection: "column",
                gap: "20px",
                marginBottom: "20px",
              }}
            >
              {checkboxesToMap.map((el, idx) => {
                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      gap: "40px",
                      flexDirection: "row",
                    }}
                  >
                    <CheckboxV2
                      onChange={el.onChange}
                      text={el.text}
                      value={el.value}
                      customStyle={{
                        maxWidth: "400px",
                        width: "100%",
                        flexGrow: 1,
                      }}
                      width="auto"
                    />
                    {el.withTextInput ? (
                      <CustomInputV2
                        id="calenderWeek"
                        label="Kalender Woche"
                        onChange={(v) => setCalendarWeek(+v)}
                        value={calendarWeek.toString()}
                        width="150"
                        isDisabled={selectedCheckbox !== "calenderWeek"}
                        regexp={/^[0-9]{0,2}$/}
                      />
                    ) : null}
                    {el.withCalendarInput ? (
                      <div
                        style={{
                          display: "flex",
                          gap: "20px",
                          alignItems: "center",
                        }}
                      >
                        <CustomInputV2
                          id="weekDay"
                          label="Tag der Woche"
                          onChange={(v) => setToenisvorstWeekDay(v)}
                          value={toenisvorstWeekDay}
                          width="150"
                          isDisabled={selectedCheckbox !== "toenisvorst"}
                        />
                        {/* <CustomDatePicker label="Datumpicker" name="toenisvorstWeekDate" type=""/> */}
                        <div
                          style={{
                            marginBottom: "20px",
                          }}
                        >
                          <label
                            translate="no"
                            className={`${style.label} ${selectedCheckbox !== "toenisvorst" ? style.disabled : ""}`}
                          >
                            Datum wählen
                          </label>
                          <DatePicker
                            onChange={(v) => setToenisvorstWeekDate(v)}
                            selected={toenisvorstWeekDate}
                            locale={"de"}
                            dateFormat="dd/MM/yyyy"
                            disabled={selectedCheckbox !== "toenisvorst"}
                            className={`${style.calendar} ${selectedCheckbox !== "toenisvorst" ? style.disabled : ""}`}
                          />
                        </div>
                      </div>
                    ) : null}
                    {el.withTextAreaInput ? (
                      <TextAreaV2
                        onChange={(v) => setText(v)}
                        value={text}
                        customStyle={{
                          flexShrink: 1,
                          flexGrow: 0,
                          width: "400px",
                        }}
                        isDisabled={selectedCheckbox !== "stornieren"}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button
                text="Akzeptieren"
                width="200"
                isOutlined
                outlineColor={
                  !selectedCheckbox ||
                  (selectedCheckbox === "toenisvorst" &&
                    (!toenisvorstWeekDate || !toenisvorstWeekDay)) ||
                  tonneDefektDataItem?.status === "accepted" ||
                  (selectedCheckbox === "calenderWeek" && calendarWeek === 0)
                    ? transformHexToRgba(appConfig.mainColor, "0.4")
                    : appConfig.mainColor
                }
                textColor={
                  !selectedCheckbox ||
                  tonneDefektDataItem?.status === "accepted" ||
                  (selectedCheckbox === "calenderWeek" && calendarWeek === 0) ||
                  (selectedCheckbox === "toenisvorst" &&
                    (!toenisvorstWeekDate || !toenisvorstWeekDay)) ||
                  (selectedCheckbox === "stornieren" && !text)
                    ? transformHexToRgba(appConfig.mainColor, "0.4")
                    : appConfig.mainColor
                }
                disabled={
                  !selectedCheckbox ||
                  tonneDefektDataItem?.status === "accepted" ||
                  (selectedCheckbox === "toenisvorst" &&
                    (!toenisvorstWeekDate || !toenisvorstWeekDay)) ||
                  (selectedCheckbox === "calenderWeek" && calendarWeek === 0)
                }
                marginRight="20"
                onClick={() => {
                  if (id) {
                    dispatch(
                      updateStatusById({
                        id: id,
                        status:
                          selectedCheckbox === "stornieren"
                            ? "declined"
                            : "accepted",
                        message: generateMessage(selectedCheckbox),
                        // message:
                        //   selectedCheckbox === "calenderWeek"
                        //     ? "Der Auftrag wird nächste Woche durchgeführt."
                        //     : `Der Auftrag wird voraussichtlich in Kalenderwoche ${calendarWeek} durchgeführt.`,
                        calenderWeek: generateCalenderWeek(selectedCheckbox),
                        updatedStatusType: selectedCheckbox,
                        // calenderWeek:
                        //   selectedCheckbox === "calenderWeek"
                        //     ? (getWeek(new Date()) + 1).toString()
                        //     : calendarWeek.toString(),
                      }),
                    );
                  }
                }}
              />
              {/* <Button
                text="Stornieren"
                width="200"
                backgroundColor="white"
                isOutlined
                outlineColor={
                  tonneDefektDataItem?.status === "declined"
                    ? transformHexToRgba("#ff0000", "0.4")
                    : "red"
                }
                textColor={
                  tonneDefektDataItem?.status === "declined"
                    ? transformHexToRgba("#ff0000", "0.4")
                    : "red"
                }
                disabled={tonneDefektDataItem?.status === "declined"}
                marginRight="20"
                onClick={() => {
                  if (id) {
                    dispatch(
                      updateStatusById({
                        id: id,
                        status: "declined",
                      }),
                    );
                  }
                }}
              /> */}
            </div>
          </div>
        )}
      </AdminContentWrapper>
      {isAdminLoaderActive && <Loader />}
    </div>
  );
};

export default AdminTonneDefektById;
